import { Card, Col, Divider, Popconfirm, Row, Space, Switch } from 'antd';
import { ReactElement, useEffect, useState } from 'react';
import { ActiveChannels } from '../common/channelsEnum';
import Paragraph from 'antd/lib/typography/Paragraph';
import Text from 'antd/lib/typography/Text';
import { ExclamationCircleTwoTone } from '@ant-design/icons';
import { getChannelReadableName } from '../../utils/readable';
import { api } from '../../utils/axiosConfig';
import { Environments } from '../../api/environments/interface';
import { EnvironmentSettings } from '../../interfaces/backend/EnvironmentSettings';

export interface EnvironmentContainerProps {
  environment: Environments;
}

const EnvironmentContainer = ({
  environment
}: EnvironmentContainerProps): ReactElement => {
  const [settings, setSettings] = useState<EnvironmentSettings>();
  const [channelLoading, setChannelLoading] = useState<ActiveChannels>();

  const getEnvironmentSettings = (clientId: string) => {
    api('get', `/${clientId}`).then((res) => {
      const data = res.data as EnvironmentSettings;

      setSettings(data);
      setChannelLoading(undefined);
    });
  };

  useEffect(() => {
    getEnvironmentSettings(environment.clientId);
  }, [environment.clientId]);

  const redactSecret = (str: string): string => {
    let displayText = '';

    for (let i = 0; i < str.length; i++) {
      if (i < 3 || i > str.length - 4) {
        displayText = displayText.concat(str.charAt(i));
      } else {
        displayText = displayText.concat('*');
      }
    }

    return displayText;
  };

  const updateDisabledChannels = (channel: ActiveChannels) => {
    // eslint-disable-next-line
    const disableSending = [...settings!.disableSending!];

    if (disableSending.includes(channel)) {
      const idx = disableSending.findIndex((item) => item === channel);

      disableSending.splice(idx, 1);
    } else {
      disableSending.push(channel);
    }

    setChannelLoading(channel);
    api('patch', `/${environment.clientId}`, {
      disableSending
    }).then(() => {
      getEnvironmentSettings(environment.clientId);
    });
  };

  return (
    <>
      {!settings && (
        <Card loading title="Loading" style={{ marginBottom: '1%' }} />
      )}

      {settings && (
        <Card
          loading={!settings}
          title={`${environment.title} Environment`}
          style={{
            marginBottom: '1%'
          }}
        >
          <div>
            <Row>
              <Col span={3}>Client Id</Col>
              <Col>
                <Text code copyable={{ text: environment.clientId }}>
                  {redactSecret(environment.clientId)}
                </Text>
              </Col>
            </Row>
            <Row>
              <Col span={3}>Client Secret</Col>
              <Col>
                <Text code copyable={{ text: environment.clientSecret }}>
                  {redactSecret(environment.clientSecret)}
                </Text>
              </Col>
            </Row>
          </div>
          <Divider>
            <Space>Environment Channels</Space>
          </Divider>
          <Paragraph style={{ marginBottom: '20px' }}>
            Disable or enable channels for an environment. Notifications are
            still sent to NotificationAPI while disabled, but are not delivered
            to the end user. Disabling channels can be useful for debugging
            notifications without affecting end-users or preventing accidental
            deliveries.
          </Paragraph>
          <div style={{ marginBottom: '20px' }}>
            {Object.values(ActiveChannels).map((channel) => {
              const isChannelDisabled =
                // eslint-disable-next-line
                settings!.disableSending!.includes(channel);

              return (
                <Row key={channel} style={{ marginBottom: '4px' }}>
                  <Popconfirm
                    title={`Disabling Confirmation`}
                    description={`Are you sure you want to disable ${getChannelReadableName(
                      channel
                    )}?`}
                    icon={<ExclamationCircleTwoTone twoToneColor="#ff4d4f" />}
                    okText="Disable"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => updateDisabledChannels(channel)}
                    disabled={isChannelDisabled}
                  >
                    <Switch
                      checked={!isChannelDisabled}
                      loading={channelLoading === channel}
                      key={`${channel}_switch`}
                      style={{ marginRight: '4px' }}
                      onChange={() => {
                        if (isChannelDisabled) {
                          updateDisabledChannels(channel);
                        }
                      }}
                    />
                  </Popconfirm>
                  {getChannelReadableName(channel)}
                </Row>
              );
            })}
          </div>
        </Card>
      )}
    </>
  );
};

export default EnvironmentContainer;
